import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import { LayoutDashboard, Database, Bell, Search, Filter, Plus, TrendingUp, Users, DollarSign, Sparkles } from "lucide-react";

export const THEME_OPTIONS = {
  a: {
    name: "Carbon & Lime", tag: "Sharp, focused, Linear-esque. Near-black actions with an electric lime signal.",
    vars: {
      "--canvas": "#F4F4F1", "--surface": "#FFFFFF", "--surface-2": "#EBEBE6",
      "--ink": "#101210", "--ink-2": "#2A2E2B", "--ink-3": "#6B7370",
      "--brand-ocean": "#191C1A", "--brand-mint": "#65A30D",
      "--glass-bg": "rgba(255,255,255,0.85)", "--glass-border": "rgba(16,18,16,0.12)",
      "--radius-card": "14px", "--radius-widget": "14px", "--radius-control": "9px",
      "--shadow-glass": "0 8px 30px rgba(16,18,16,0.08)",
    },
    charts: ["#191C1A", "#84CC16", "#5B8DEF", "#F2B233", "#E24B5B", "#8B5CF6"],
  },
  b: {
    name: "Cobalt Pop", tag: "Energetic and confident. Vivid cobalt paired with tangerine highlights.",
    vars: {
      "--canvas": "#F1F3F9", "--surface": "#FFFFFF", "--surface-2": "#E7EBF5",
      "--ink": "#0A1029", "--ink-2": "#232B4A", "--ink-3": "#5A6284",
      "--brand-ocean": "#2B4EFF", "--brand-mint": "#FF6B35",
      "--glass-bg": "rgba(255,255,255,0.8)", "--glass-border": "rgba(43,78,255,0.16)",
      "--radius-card": "20px", "--radius-widget": "20px", "--radius-control": "12px",
      "--shadow-glass": "0 18px 55px rgba(43,78,255,0.10)",
    },
    charts: ["#2B4EFF", "#FF6B35", "#00C2A8", "#FFB020", "#8B5CF6", "#EC4899"],
  },
  b1: {
    name: "Cobalt Pop", tag: "Vivid cobalt + tangerine. Energetic, high-contrast highlights.",
    vars: {
      "--canvas": "#F1F3F9", "--surface": "#FFFFFF", "--surface-2": "#E7EBF5",
      "--ink": "#0A1029", "--ink-2": "#232B4A", "--ink-3": "#5A6284",
      "--brand-ocean": "#2B4EFF", "--brand-mint": "#FF6B35",
      "--glass-bg": "rgba(255,255,255,0.8)", "--glass-border": "rgba(43,78,255,0.16)",
      "--radius-card": "20px", "--radius-widget": "20px", "--radius-control": "12px",
      "--shadow-glass": "0 18px 55px rgba(43,78,255,0.10)",
    },
    charts: ["#2B4EFF", "#FF6B35", "#00C2A8", "#FFB020", "#8B5CF6", "#EC4899", "#0EA5E9", "#64748B"],
  },
  b2: {
    name: "Cobalt Frost", tag: "Cooler and calmer. Cobalt + aqua-teal on an icy blue canvas.",
    vars: {
      "--canvas": "#EDF2FA", "--surface": "#FFFFFF", "--surface-2": "#E2EAF6",
      "--ink": "#0C1B33", "--ink-2": "#27395A", "--ink-3": "#5D6E8C",
      "--brand-ocean": "#2446E6", "--brand-mint": "#00B8A9",
      "--glass-bg": "rgba(255,255,255,0.82)", "--glass-border": "rgba(36,70,230,0.14)",
      "--radius-card": "18px", "--radius-widget": "18px", "--radius-control": "11px",
      "--shadow-glass": "0 14px 44px rgba(36,70,230,0.10)",
    },
    charts: ["#2446E6", "#00B8A9", "#38BDF8", "#FFB020", "#6366F1", "#94A3B8", "#0E7490", "#F45D8C"],
  },
  b3: {
    name: "Cobalt Noir", tag: "Deep navy ink + cobalt with warm amber accents. Sharp and premium.",
    vars: {
      "--canvas": "#F5F6F8", "--surface": "#FFFFFF", "--surface-2": "#EAECF2",
      "--ink": "#0A0F2E", "--ink-2": "#252B4E", "--ink-3": "#5E6480",
      "--brand-ocean": "#1F3BD1", "--brand-mint": "#FFB020",
      "--glass-bg": "rgba(255,255,255,0.85)", "--glass-border": "rgba(10,15,46,0.14)",
      "--radius-card": "14px", "--radius-widget": "14px", "--radius-control": "9px",
      "--shadow-glass": "0 10px 36px rgba(10,15,46,0.12)",
    },
    charts: ["#1F3BD1", "#FFB020", "#FF6B35", "#00C2A8", "#0A0F2E", "#8B5CF6", "#38BDF8", "#94A3B8"],
  },
  c: {
    name: "Evergreen & Brass", tag: "Calm, premium, heritage. Deep forest green with warm brass accents on cream.",
    vars: {
      "--canvas": "#F7F5EF", "--surface": "#FFFFFF", "--surface-2": "#EEEBE0",
      "--ink": "#152019", "--ink-2": "#2C3A31", "--ink-3": "#66705F",
      "--brand-ocean": "#1E5C46", "--brand-mint": "#C99127",
      "--glass-bg": "rgba(255,255,255,0.85)", "--glass-border": "rgba(30,92,70,0.18)",
      "--radius-card": "18px", "--radius-widget": "18px", "--radius-control": "11px",
      "--shadow-glass": "0 14px 45px rgba(30,92,70,0.10)",
    },
    charts: ["#1E5C46", "#C99127", "#4E8D7C", "#B4552D", "#2F6F8F", "#7A6C5D"],
  },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
const barOpt = (colors) => ({
  color: colors, grid: { left: 40, right: 12, top: 24, bottom: 26 },
  xAxis: { type: "category", data: MONTHS, axisLine: { show: false }, axisTick: { show: false } },
  yAxis: { type: "value", splitLine: { lineStyle: { opacity: 0.35 } } },
  series: [
    { type: "bar", name: "Revenue", data: [42, 58, 51, 76, 69, 88], barWidth: 14, itemStyle: { borderRadius: [6, 6, 0, 0] } },
    { type: "bar", name: "Costs", data: [28, 34, 30, 44, 39, 47], barWidth: 14, itemStyle: { borderRadius: [6, 6, 0, 0] } },
  ], legend: { top: 0, right: 0, itemWidth: 10, itemHeight: 10 },
});
const lineOpt = (colors) => ({
  color: colors.slice(1), grid: { left: 40, right: 12, top: 24, bottom: 26 },
  xAxis: { type: "category", data: MONTHS, boundaryGap: false, axisLine: { show: false }, axisTick: { show: false } },
  yAxis: { type: "value", splitLine: { lineStyle: { opacity: 0.35 } } },
  series: [{ type: "line", name: "Active users", data: [320, 410, 395, 520, 610, 720], smooth: true, areaStyle: { opacity: 0.12 }, lineWidth: 3, symbol: "circle" }],
});
const donutOpt = (colors) => ({
  color: colors, tooltip: { trigger: "item" }, legend: { bottom: 0, itemWidth: 10, itemHeight: 10 },
  series: [{ type: "pie", radius: ["52%", "74%"], center: ["50%", "44%"], itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
    data: [{ value: 44, name: "North" }, { value: 28, name: "South" }, { value: 18, name: "East" }, { value: 10, name: "West" }], label: { show: false } }],
});

function Kpi({ icon: Icon, label, value, delta, accent }) {
  return (
    <div className="glass-card p-4 flex-1 min-w-[150px]">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[color:var(--ink-3)]">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="font-display text-2xl text-[color:var(--ink)] mt-1">{value}</div>
      <div className="text-xs mt-0.5" style={{ color: accent }}>{delta}</div>
    </div>
  );
}

export default function ThemePreview() {
  const [sp] = useSearchParams();
  const opt = THEME_OPTIONS[sp.get("opt")] || THEME_OPTIONS.a;
  const primary = opt.vars["--brand-ocean"];
  const accent = opt.vars["--brand-mint"];
  return (
    <div style={opt.vars} className="min-h-screen bi-canvas" data-testid="theme-preview">
      <header className="h-14 px-5 flex items-center justify-between border-b border-[color:var(--glass-border)] bg-[color:var(--glass-bg)] backdrop-blur-md">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white" style={{ background: primary }}><Sparkles className="w-4 h-4" /></div>
            <span className="font-display font-semibold text-[color:var(--ink)]">Eazy Analytics</span>
          </div>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <span className="px-3 py-1.5 rounded-lg text-white flex items-center gap-1.5" style={{ background: primary }}><LayoutDashboard className="w-3.5 h-3.5" />Dashboards</span>
            <span className="px-3 py-1.5 rounded-lg text-[color:var(--ink-3)] flex items-center gap-1.5"><Database className="w-3.5 h-3.5" />Data</span>
            <span className="px-3 py-1.5 rounded-lg text-[color:var(--ink-3)] flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" />Alerts</span>
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2 text-xs text-[color:var(--ink-3)] bg-[color:var(--surface)] border border-[color:var(--glass-border)] rounded-lg px-3 py-1.5" style={{ borderRadius: "var(--radius-control)" }}><Search className="w-3.5 h-3.5" />Search…</div>
          <button className="text-white text-sm px-3.5 py-1.5 flex items-center gap-1" style={{ background: primary, borderRadius: "var(--radius-control)" }}><Plus className="w-4 h-4" />New Dashboard</button>
        </div>
      </header>
      <div className="px-5 py-3 flex items-center gap-2 text-xs">
        <span className="flex items-center gap-1 text-[color:var(--ink-3)]"><Filter className="w-3.5 h-3.5" />Filters</span>
        <span className="px-2.5 py-1 rounded-full text-white" style={{ background: accent }}>Region = North ✕</span>
        <span className="px-2.5 py-1 rounded-full bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[color:var(--ink-2)]">Last 6 months</span>
        <span className="ml-auto font-display text-sm text-[color:var(--ink)]">Theme: <b>{opt.name}</b> — {opt.tag}</span>
      </div>
      <div className="px-5 pb-6 space-y-4">
        <div className="flex gap-3 flex-wrap">
          <Kpi icon={DollarSign} label="Revenue" value="$1.28M" delta="▲ 12.4% vs last period" accent={accent} />
          <Kpi icon={Users} label="Active users" value="24,318" delta="▲ 8.1%" accent={accent} />
          <Kpi icon={TrendingUp} label="Conversion" value="4.7%" delta="▲ 0.6 pts" accent={accent} />
          <Kpi icon={LayoutDashboard} label="Avg. order" value="$86.20" delta="▼ 1.2%" accent={primary} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="glass-card p-4 lg:col-span-2">
            <div className="text-sm font-medium text-[color:var(--ink)] mb-1">Revenue vs costs</div>
            <ReactECharts option={barOpt(opt.charts)} style={{ height: 230 }} notMerge />
          </div>
          <div className="glass-card p-4">
            <div className="text-sm font-medium text-[color:var(--ink)] mb-1">Sales by region</div>
            <ReactECharts option={donutOpt(opt.charts)} style={{ height: 230 }} notMerge />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="glass-card p-4 lg:col-span-2">
            <div className="text-sm font-medium text-[color:var(--ink)] mb-1">Active users trend</div>
            <ReactECharts option={lineOpt(opt.charts)} style={{ height: 200 }} notMerge />
          </div>
          <div className="glass-card p-4 flex flex-col justify-center gap-2">
            <div className="text-sm font-medium text-[color:var(--ink)]">Compare options live</div>
            <div className="flex gap-2">
              {Object.entries(THEME_OPTIONS).map(([k, o]) => (
                <Link key={k} to={`/theme-preview?opt=${k}`}
                  className={`text-xs px-3 py-1.5 border ${o.name === opt.name ? "text-white" : "text-[color:var(--ink-2)] bg-[color:var(--surface)]"}`}
                  style={{ borderRadius: "var(--radius-control)", background: o.name === opt.name ? primary : undefined, borderColor: "var(--glass-border)" }}>
                  {o.name}
                </Link>
              ))}
            </div>
            <p className="text-[11px] text-[color:var(--ink-3)]">Chart palettes, buttons, chips, cards and canvas all follow the selected theme.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
